import { zodResolver } from '@hookform/resolvers/zod';
import type { IssueType, Project, Workflow } from '@servicetim/shared';
import { useCreateIssueType, useUpdateIssueType } from '@servicetim/ui/api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  Textarea,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { problemDetail } from '../lib/field-errors';
import { applyProblemErrors } from '../lib/rhf-errors';
import { FormRow } from '../ui/form-row';
import { SimpleSelect } from '../ui/simple-select';

const IssueTypeFormSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(60),
  description: z.string().trim().max(500),
  icon: z.string().trim().max(40),
  workflowId: z.uuid('Choose a published workflow'),
  isSubtask: z.boolean(),
  portalVisible: z.boolean(),
  portalGroup: z.string().nullable(),
  portalHelpText: z.string().trim().max(500),
});
type IssueTypeFormValues = z.infer<typeof IssueTypeFormSchema>;

const toValues = (t: IssueType | null, fallbackWorkflowId: string): IssueTypeFormValues => ({
  name: t?.name ?? '',
  description: t?.description ?? '',
  icon: t?.icon ?? '',
  workflowId: t?.workflowId ?? fallbackWorkflowId,
  isSubtask: t?.isSubtask ?? false,
  portalVisible: t?.portal.visible ?? true,
  portalGroup: t?.portal.group ?? null,
  portalHelpText: t?.portal.helpText ?? '',
});

export interface IssueTypeDialogProps {
  project: Project;
  workflows: readonly Workflow[];
  /** `null` creates a new type. */
  issueType: IssueType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Create / edit an issue type; only published workflows can be bound (others are listed disabled). */
export function IssueTypeDialog({
  project,
  workflows,
  issueType,
  open,
  onOpenChange,
}: IssueTypeDialogProps) {
  const id = useId();
  const create = useCreateIssueType(project.id);
  const update = useUpdateIssueType(project.id);
  const [omitPaths, setOmitPaths] = useState<Set<string>>(new Set());
  const published = workflows.filter((w) => w.publishedVersion !== null);
  const firstPublished = published[0]?.id ?? '';

  const form = useForm<IssueTypeFormValues>({
    resolver: zodResolver(IssueTypeFormSchema),
    defaultValues: toValues(issueType, firstPublished),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the dialog opens or the row changes
  useEffect(() => {
    if (open) {
      form.reset(toValues(issueType, firstPublished));
      create.reset();
      update.reset();
      setOmitPaths(new Set());
    }
  }, [open, issueType, firstPublished]);

  const workflowOptions = useMemo(
    () =>
      workflows.map((w) => ({
        value: w.id,
        label: w.publishedVersion === null ? `${w.name} (not published)` : w.name,
        disabled: w.publishedVersion === null && w.id !== issueType?.workflowId,
      })),
    [workflows, issueType?.workflowId],
  );
  const categoryOptions = project.settings.portalCategories.map((c) => ({
    value: c.key,
    label: c.label,
  }));

  const error = create.error ?? update.error;
  const detail = problemDetail(error);
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    const portal = {
      visible: values.portalVisible,
      group: values.portalGroup,
      helpText: values.portalHelpText === '' ? null : values.portalHelpText,
    };
    const common = {
      name: values.name,
      description: values.description === '' ? null : values.description,
      icon: values.icon === '' ? null : values.icon,
      workflowId: values.workflowId,
      isSubtask: values.isSubtask,
    };
    try {
      if (issueType) {
        await update.mutateAsync({ id: issueType.id, patch: { ...common, portal } });
        toast.success(`Issue type "${values.name}" updated`);
      } else {
        await create.mutateAsync({ ...common, portal });
        toast.success(`Issue type "${values.name}" created`);
      }
      onOpenChange(false);
    } catch (caught) {
      setOmitPaths(
        applyProblemErrors(caught, form.setError, {
          known: ['name', 'description', 'icon', 'workflowId', 'isSubtask'],
        }),
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{issueType ? `Edit ${issueType.name}` : 'New issue type'}</DialogTitle>
          <DialogDescription>
            Tickets of this type follow the selected workflow from its initial status.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(event);
          }}
        >
          {error && detail !== 'workflow.not_published' && detail !== 'issue_type.name_taken' ? (
            <ProblemBanner error={error} omitPaths={omitPaths} size="sm" />
          ) : null}
          <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
            <FormRow
              label="Name"
              htmlFor={`${id}-name`}
              required
              error={
                detail === 'issue_type.name_taken'
                  ? 'Another issue type already has this name'
                  : errors.name?.message
              }
            >
              <Input id={`${id}-name`} {...form.register('name')} maxLength={60} autoFocus />
            </FormRow>
            <FormRow label="Icon" htmlFor={`${id}-icon`} hint="Emoji or icon name">
              <Input id={`${id}-icon`} {...form.register('icon')} maxLength={40} />
            </FormRow>
          </div>
          <FormRow
            label="Description"
            htmlFor={`${id}-description`}
            error={errors.description?.message}
          >
            <Textarea
              id={`${id}-description`}
              {...form.register('description')}
              rows={2}
              maxLength={500}
            />
          </FormRow>
          <Controller
            control={form.control}
            name="workflowId"
            render={({ field, fieldState }) => (
              <FormRow
                label="Workflow"
                htmlFor={`${id}-workflow`}
                required
                error={
                  detail === 'workflow.not_published'
                    ? 'This workflow has never been published. Publish it first.'
                    : fieldState.error?.message
                }
                hint={
                  published.length === 0
                    ? 'No workflow of this project is published yet. Publish one from the Workflows tab first.'
                    : 'Only published workflows can be bound.'
                }
              >
                <SimpleSelect
                  id={`${id}-workflow`}
                  value={field.value || undefined}
                  onChange={(next) => field.onChange(next ?? '')}
                  options={workflowOptions}
                  placeholder="Choose a workflow…"
                  invalid={fieldState.invalid}
                />
              </FormRow>
            )}
          />
          <Controller
            control={form.control}
            name="isSubtask"
            render={({ field }) => (
              <FormRow
                label="Sub-task type (children of other tickets)"
                htmlFor={`${id}-subtask`}
                inline
              >
                <Checkbox
                  id={`${id}-subtask`}
                  checked={field.value}
                  onCheckedChange={(next) => field.onChange(next === true)}
                />
              </FormRow>
            )}
          />
          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 font-medium text-fg-muted text-sm">Portal</legend>
            <Controller
              control={form.control}
              name="portalVisible"
              render={({ field }) => (
                <FormRow
                  label="Requesters can raise this type in the portal"
                  htmlFor={`${id}-visible`}
                  inline
                >
                  <Checkbox
                    id={`${id}-visible`}
                    checked={field.value}
                    onCheckedChange={(next) => field.onChange(next === true)}
                  />
                </FormRow>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={form.control}
                name="portalGroup"
                render={({ field }) => (
                  <FormRow
                    label="Portal category"
                    htmlFor={`${id}-group`}
                    hint={
                      categoryOptions.length === 0 ? 'Define categories in Settings.' : undefined
                    }
                  >
                    <SimpleSelect
                      id={`${id}-group`}
                      value={field.value}
                      onChange={(next) => field.onChange(next ?? null)}
                      options={categoryOptions}
                      emptyLabel="No category"
                      disabled={categoryOptions.length === 0}
                    />
                  </FormRow>
                )}
              />
              <FormRow
                label="Help text"
                htmlFor={`${id}-help`}
                error={errors.portalHelpText?.message}
              >
                <Input id={`${id}-help`} {...form.register('portalHelpText')} maxLength={500} />
              </FormRow>
            </div>
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {issueType ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
